import {
  Autocomplete,
  Box,
  Button,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  IconButton,
  Modal,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { ArrowDropDownIcon, CloudIcon, ImageIcon } from "../../icons";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import { localFileState, selectedProjectState } from "../../recoil";
import { useNavigate, useParams } from "react-router-dom";
import {
  IAddProject,
  IEditProject,
  IProjectData,
  IRenderFileChipsProps,
  ISumProjectData,
  ITrainData,
} from "@types";
import { handleTextFieldChange, imageFlat } from "../../utils/utils";
import {
  createProject,
  putDatasetImages,
  editProject,
  getProject,
} from "../../services/projectService";
import { getTrain, getTrains } from "../../services/trainService";
import TrainModal from "../train/TrainModal";
import { toast } from "react-toastify";
import AnalysisDataset from "../AnalysisDataset";
import CustomAutoComplete from "../customComponent/CustomAutoComplete";
import { t } from "i18next";
import { ServerModalStyle } from "../../styles/GlobalStyles";
import ServerFileModal from "../ServerFileModal";
import { RenderFileChips } from "../../utils/utilsComponent";
import { isEmptyObj } from "common-lib";

interface ProjectModalProps {
  open: boolean;
  onClose: () => void;
  state: string;
  initStep: string;
  projectCardId: number | null;
  data: IAddProject;
}

interface SelectTrainProps {
  selectedTrain: ITrainData | null;
  onTrainChange: (selectedTrain: ITrainData | null) => void;
}

export function SelectTrain({
  selectedTrain,
  onTrainChange,
}: SelectTrainProps) {
  const [trainList, setTrainList] = useState<ITrainData[]>([]);
  const [train, setTrain] = useState<ITrainData | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getTrains();
        if (response) {
          setTrainList(response.trains);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  }, []);

  const handleChange = (event: any, value: ITrainData | null) => {
    onTrainChange(value);
  };

  return (
    <>
      <Autocomplete
        disablePortal={false}
        options={trainList}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => <TextField {...params} label="학습" />}
        value={
          trainList.find((train) => train.id === selectedTrain?.id) || null
        }
        onChange={handleChange}
        sx={{ flex: 1 }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
      {/* <CustomAutoComplete value={train} setValue={setTrain} width="100%" /> */}
    </>
  );
}

interface FormData {
  project_name?: string;
  step?: string;
  material_name?: string;
  images?: string[];
  train?: ITrainData | null;
}

export default function ProjectModal({
  open,
  onClose,
  initStep,
  state,
}: ProjectModalProps) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<boolean>(false);
  const [openDataset, setOpenDataset] = useState<boolean>(false);
  const [openTrain, setOpenTrain] = useState<boolean>(false);
  const selectedProject = useRecoilValue(selectedProjectState);
  // const [serverFiles, setServerFiles] = useRecoilState(serverFilesNameState);
  const [serverFiles, setServerFiles] = useState<string[]>([]);
  const [localFiles, setlocalFiles] = useRecoilState(localFileState);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialFiles, setInitialFiles] = useState<string[]>([]);
  const [projectsData, setProjectsData] = useState<IProjectData[]>([]);

  const [formData, setFormData] = useState<FormData>({});
  const [project_name, setProjectName] = useState<string>("");
  const [step, setStep] = useState<string>("분류");
  const [material_name, setMaterialName] = useState<string>("");
  const [images, setImages] = useState<string[]>([]);
  const [train, setTrain] = useState<ITrainData | null>(null);
  let projectId: number | null = Number(id) || selectedProject;

  const getProjectData = async () => {
    setIsLoading(true);
    if (id) {
      projectId = Number(id);
    } else {
      projectId = selectedProject;
    }

    if (projectId) {
      try {
        const response = await getProject(projectId);
        const paths = imageFlat(response.project.images).map(
          (image: { path: string }) => image.path
        );
        const projectData = response.project;

        setFormData({
          ...projectData,
          images: paths,
        });
        setProjectName(projectData.project_name);
        setStep(projectData.step);
        setMaterialName(projectData.material_name);
        setImages(projectData.images);
        setTrain(projectData.train);
        setServerFiles(paths);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (state !== "add") {
      getProjectData();
    } else {
      setStep(initStep);
      setInitialFiles([]);
      setServerFiles([]);
    }
  }, []);

  const stateData: FormData = {
    project_name,
    step,
    material_name,
    images,
    train,
  };

  const getChangedValues = (): Partial<FormData> => {
    const updatedValues: Record<string, any> = {};

    Object.keys(formData).forEach((key) => {
      const typedKey = key as keyof FormData;
      const formDataValue = formData[typedKey]; // 기존 데이터
      const stateDataValue = stateData[typedKey]; // 입력된 데이터
      if (stateDataValue !== undefined && formDataValue !== stateDataValue) {
        updatedValues[typedKey] = stateDataValue as FormData[typeof typedKey];
      }
    });

    return updatedValues;
  };

  const changes = getChangedValues();

  useEffect(() => {
    setImages(serverFiles);
  }, [serverFiles]);

  const handleToggleChange = (
    e: React.MouseEvent<HTMLElement>,
    newType: string
  ) => {
    if (newType !== null && Object.keys(formData)) {
      setStep(newType);
    }
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleTrainChange = (selectedTrain: ITrainData | null) => {
    setTrain(selectedTrain);
  };

  // 저장
  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    try {
      const projectData: IAddProject = {
        project_name: project_name,
        step: step,
        material_name: material_name,
        images: images,
        train_id: train?.id || null,
      };

      await createProject(projectData);
      navigate("/home");
    } catch (error) {
      console.error(error);
    } finally {
      setServerFiles([]);
      toast.success(t("SuccessSaveMsg"));
      onClose();
    }
  };

  // 수정
  const handleEdit = async () => {
    try {
      if (projectId) {
        const { train, images, ...restChanges } = changes;
        const projectData: IEditProject = {
          ...restChanges,
          train_id: changes.train?.id || null,
        };

        if (!isEmptyObj(changes)) {
          await editProject(projectId, projectData);
        }

        if (changes.images) {
          await putDatasetImages(projectId, serverFiles);
        }

        const response = await getProject(projectId);

        setProjectsData((prevDatas) =>
          prevDatas.map((project) =>
            project.id === projectId
              ? { ...project, ...response.project }
              : project
          )
        );
        toast.success(response.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      toast.success(t("SuccessEditMsg"));
      onClose();
    }
  };

  const handleCancel = () => {
    if (state === "add") {
      navigate("/home");
    }

    if (!isEmptyObj(changes)) {
      if (window.confirm("변경사항은 저장되지 않을 수 있습니다.")) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleDeleteServerFiles = () => {
    setServerFiles([]);
  };

  return (
    <Dialog open={open}>
      <Paper
        sx={{
          width: "600px",
        }}
      >
        <Typography variant="h5" sx={{ p: 2 }}>
          {state === "add" ? t("CreateProject") : t("EditProject")}
        </Typography>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <>
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                alignContent: "center",
                gap: "20px",
                p: "20px",
              }}
            >
              <ToggleButtonGroup
                value={step}
                exclusive
                onChange={handleToggleChange}
                disabled={initStep === "분석"}
              >
                <ToggleButton value="분류" size="large" sx={{ width: "50%" }}>
                  {t("Classification")}
                </ToggleButton>
                <ToggleButton value="분석" size="large" sx={{ width: "50%" }}>
                  {t("Analysis")}
                </ToggleButton>
              </ToggleButtonGroup>
              {step === "분류" ? (
                <>
                  <TextField
                    required
                    id="outlined-required"
                    label={t("ProjectName")}
                    onChange={handleTextFieldChange(setProjectName)}
                    value={project_name || ""}
                  />
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body1">
                        {t("DatasetSetting")}
                      </Typography>
                      <Box sx={{ display: "flex", gap: "10px" }}>
                        <Button
                          variant="contained"
                          size="large"
                          onClick={() => {
                            setOpenDataset(true);
                          }}
                        >
                          {t("SelectServerFile")}
                        </Button>
                      </Box>
                    </Box>
                    {/* {(serverFiles.length > 0 || localFiles.length > 0) && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          mt: 2,
                        }}
                      >
                        {serverFiles.length > 0 && (
                          <RenderFileChips
                            files={serverFiles}
                            icon={<CloudIcon />}
                            handleDelete={handleDeleteServerFiles}
                          />
                        )}
                      </Box>
                    )} */}
                  </Box>
                  <TextField
                    id="outlined-required"
                    label={t("MaterialName")}
                    onChange={handleTextFieldChange(setMaterialName)}
                    value={material_name || ""}
                  />
                  <CardActions>
                    <IconButton onClick={handleExpandClick}>
                      <ArrowDropDownIcon />
                    </IconButton>
                    {t("MoreOptions")}
                  </CardActions>
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      <SelectTrain
                        selectedTrain={train}
                        onTrainChange={handleTrainChange}
                      />
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => {
                          setOpenTrain(true);
                        }}
                      >
                        {t("CreateTrain")}
                      </Button>
                    </Box>
                  </Collapse>
                </>
              ) : (
                <>
                  <Typography>분석 Content</Typography>
                  {(serverFiles.length > 0 || localFiles.length > 0) && (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        mt: 2,
                      }}
                    >
                      {serverFiles.length > 0 && (
                        <RenderFileChips
                          files={serverFiles}
                          icon={<CloudIcon />}
                          handleDelete={handleDeleteServerFiles}
                        />
                      )}
                    </Box>
                  )}
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => {
                      setOpenDataset(true);
                    }}
                  >
                    {t("SelectServerFile")}
                  </Button>
                </>
              )}
            </CardContent>

            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 1, p: 1 }}
            >
              {state === "add" ? (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSubmit}
                  >
                    {t("Create")}
                  </Button>
                  <Button variant="text" size="large" onClick={handleCancel}>
                    {t("Cancel")}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="contained" size="large" onClick={handleEdit}>
                    {t("Edit")}
                  </Button>
                  <Button variant="text" size="large" onClick={handleCancel}>
                    {t("Cancel")}
                  </Button>
                </>
              )}
            </Box>
          </>
        )}
      </Paper>
      {openDataset &&
        (step === "분류" ? (
          <Modal open={openDataset}>
            <Box sx={ServerModalStyle}>
              <ServerFileModal onClick={() => setOpenDataset(false)} />
            </Box>
          </Modal>
        ) : (
          <Modal open={openDataset}>
            <Box sx={ServerModalStyle}>
              <AnalysisDataset onClick={() => setOpenDataset(false)} />
            </Box>
          </Modal>
        ))}
      <TrainModal
        open={openTrain}
        onClose={() => setOpenTrain(false)}
        state="add"
        selectTrain={null}
      />
    </Dialog>
  );
}
